import React, { useContext, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import Context from '../../../utils/context';
import { PhosphorIcon, PHOSPHOR_PATHS } from '../../users/conversation_room/phosphor_icons';
import { isPhoneCallsEnabled } from '../../../helpers/feature_checker.js';

import styles from './user_menu.module.css';

const UserMenu = ({ renderTrigger } = {}) => {
  const context = useContext(Context);
  const isMobile = context.viewport.getIsMobile();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [menuReady, setMenuReady] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleClick = (e) => {
      const clickedInsideWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const clickedInsideMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!clickedInsideWrap && !clickedInsideMenu) {
        setOpen(false);
      }
    };

    const updatePosition = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      // Fallback to CSS min-width (200px) until we can measure the portal
      const fallbackWidth = 200;
      const menuWidth = menuRef.current ? menuRef.current.offsetWidth || fallbackWidth : fallbackWidth;
      const paddingRight = 10; // mirrors .menu { right: 10px }
      const desiredLeft = rect.right - menuWidth - paddingRight;
      const viewportPadding = 8;
      const clampedLeft = Math.max(
        viewportPadding,
        Math.min(desiredLeft, window.innerWidth - menuWidth - viewportPadding)
      );
      const top = rect.bottom + 8; // mirrors .menu { top: calc(100% + 8px) }
      const clampedTop = Math.max(viewportPadding, Math.min(top, window.innerHeight - viewportPadding));
      setMenuPosition({ top: clampedTop, left: clampedLeft });
    };

    updatePosition();

    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuReady(false);
      return undefined;
    }
    // As soon as the menu portal mounts, measure again for precise placement
    const raf = window.requestAnimationFrame(() => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const menuWidth = menuRef.current ? menuRef.current.offsetWidth || 200 : 200;
      const paddingRight = 10;
      const desiredLeft = rect.right - menuWidth - paddingRight;
      const viewportPadding = 8;
      const clampedLeft = Math.max(
        viewportPadding,
        Math.min(desiredLeft, window.innerWidth - menuWidth - viewportPadding)
      );
      const top = rect.bottom + 8;
      const clampedTop = Math.max(viewportPadding, Math.min(top, window.innerHeight - viewportPadding));
      setMenuPosition({ top: clampedTop, left: clampedLeft });
      setMenuReady(true);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [open]);

  const currentUser = context.auth.getCurrentUser();
  if (!currentUser || !context.auth.isLoggedIn()) return null;

  const displayName = currentUser.name || currentUser.first_name || 'Account';
  const isImpersonating = context.auth.isImpersonating && context.auth.isImpersonating();
  const isImpersonatingMentor = isImpersonating && currentUser.current_profile_type === 'PatientPartner';
  const phoneCallsEnabled = isPhoneCallsEnabled(context.auth.currentAccount());
  const canShowAdminControls = context.auth.isSuperUser && context.auth.isSuperUser();

  const closeMenu = () => setOpen(false);

  const handleLogout = () => {
    closeMenu();
    context.auth.userLogOut();
  };

  if (!canShowAdminControls) {
    if (renderTrigger) return null;

    return (
      <button type='button' className={styles.logoutButton} onClick={handleLogout}>
        Log out
      </button>
    );
  }

  const triggerClass = isMobile ? `${styles.trigger} ${styles.triggerCompact}` : styles.trigger;
  const handleToggle = () => setOpen((current) => !current);

  const menuContent = (
    <div
      ref={menuRef}
      className={styles.menu}
      style={{
        position: 'fixed',
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
        right: 'auto',
        visibility: open && menuReady ? 'visible' : 'hidden',
      }}
      role='menu'
      aria-label={`Account menu for ${displayName}`}
    >
      {isImpersonatingMentor && (
        <>
          <Link to='/member/conversations' role='menuitem' className={styles.menuItem} onClick={closeMenu}>
            <PhosphorIcon path={PHOSPHOR_PATHS.chatCircle.fill} size={16} />
            <span>Conversations</span>
          </Link>
          {phoneCallsEnabled && (
            <Link to='/member/calls' role='menuitem' className={styles.menuItem} onClick={closeMenu}>
              <PhosphorIcon path={PHOSPHOR_PATHS.phone.regular} size={16} />
              <span>My Calls</span>
            </Link>
          )}
        </>
      )}
      <Link to='/member/settings' role='menuitem' className={styles.menuItem} onClick={closeMenu}>
        <PhosphorIcon path={PHOSPHOR_PATHS.gear.regular} size={16} />
        <span>Settings</span>
      </Link>
      <button
        type='button'
        role='menuitem'
        className={`${styles.menuItem} ${styles.menuItemSignOut}`}
        onClick={handleLogout}
      >
        <PhosphorIcon path={PHOSPHOR_PATHS.signOut.regular} size={16} />
        <span>Sign out</span>
      </button>
    </div>
  );

  return (
    <div className={styles.wrap} ref={wrapRef}>
      {renderTrigger ? (
        renderTrigger({ onOpen: handleToggle, isOpen: open })
      ) : (
        <button
          type='button'
          className={triggerClass}
          onClick={handleToggle}
          aria-label={isMobile ? `Open menu for ${displayName}` : undefined}
          aria-expanded={open}
          aria-haspopup='menu'
        >
          <span className={styles.triggerAvatar} aria-hidden='true'>
            <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8'>
              <circle cx='12' cy='8' r='4' />
              <path d='M4 21c0-4 4-6 8-6s8 2 8 6' />
            </svg>
          </span>
          {!isMobile && (
            <>
              <span className={styles.name}>{displayName}</span>
              <span className={styles.caret} aria-hidden='true'>
                ▾
              </span>
            </>
          )}
        </button>
      )}
      {open ? createPortal(menuContent, document.body) : null}
    </div>
  );
};

export default UserMenu;
